import React from 'react';
import { Helmet } from 'react-helmet-async';

/**
 * Single source of truth for per-page SEO + JSON-LD structured data.
 *
 * Usage:
 *   <SEO
 *     title="Las Vegas Catering & Event Services | Eventism"
 *     description="..."
 *     keywords={['las vegas catering', ...]}
 *     image="https://..."
 *     type="website"
 *     structuredData={[ {...}, {...} ]}  // optional array of JSON-LD objects
 *   />
 *
 * Falls back to sensible Eventism brand defaults when any prop is missing.
 */

const SITE_URL = (typeof window !== 'undefined' && window.location?.origin) || 'https://eventism.com';
const DEFAULT_IMAGE = `${SITE_URL}/eventism-logo.png`;
const BRAND_NAME = 'Eventism';
const TWITTER_HANDLE = '@eventism';

export default function SEO({
  title,
  description,
  keywords,
  image,
  url,
  type = 'website',
  noindex = false,
  structuredData,
  canonical,
  publishedTime,
  modifiedTime,
  locale = 'en_US',
}) {
  const fullTitle = title
    ? (title.includes(BRAND_NAME) ? title : `${title} | ${BRAND_NAME}`)
    : `${BRAND_NAME} — Plan Your Best Event Ever`;
  const finalImage = image || DEFAULT_IMAGE;
  const finalUrl = url || canonical || (typeof window !== 'undefined' ? window.location.href : SITE_URL);
  const finalCanonical = canonical || finalUrl.split('?')[0].split('#')[0];
  const kw = Array.isArray(keywords) ? keywords.filter(Boolean).join(', ') : keywords;

  const ldArray = structuredData
    ? (Array.isArray(structuredData) ? structuredData : [structuredData]).filter(Boolean)
    : [];

  return (
    <Helmet prioritizeSeoTags>
      <title>{fullTitle}</title>
      {description && <meta name="description" content={description} />}
      {kw && <meta name="keywords" content={kw} />}
      <link rel="canonical" href={finalCanonical} />
      {noindex && <meta name="robots" content="noindex,nofollow" />}
      {!noindex && <meta name="robots" content="index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1" />}

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      {description && <meta property="og:description" content={description} />}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={finalUrl} />
      <meta property="og:image" content={finalImage} />
      <meta property="og:image:alt" content={title || BRAND_NAME} />
      <meta property="og:site_name" content={BRAND_NAME} />
      <meta property="og:locale" content={locale} />
      {publishedTime && <meta property="article:published_time" content={publishedTime} />}
      {modifiedTime && <meta property="article:modified_time" content={modifiedTime} />}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={TWITTER_HANDLE} />
      <meta name="twitter:title" content={fullTitle} />
      {description && <meta name="twitter:description" content={description} />}
      <meta name="twitter:image" content={finalImage} />

      {/* JSON-LD */}
      {ldArray.map((data, idx) => (
        <script key={`ld-${idx}`} type="application/ld+json">
          {JSON.stringify(data)}
        </script>
      ))}
    </Helmet>
  );
}
